import React, { useState, useEffect } from 'react';
import BBALoader from '../components/BBALoader';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import { startOfMonth, endOfMonth } from 'date-fns';
import { FaChevronLeft, FaChevronRight, FaPlus, FaCalendarAlt, FaClock, FaMapMarkerAlt, FaUsers, FaDollarSign, FaCogs, FaMicrochip, FaWallet, FaBullhorn } from 'react-icons/fa';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useResponsive } from '../contexts/ResponsiveContext';
import { useStrategyCalls } from '../hooks/useStrategyCalls';
import { getPillarScheduleLink } from '../services/pillarsService';
import { db } from '../firebase';
import { collection, query, where, getDocs, orderBy, limit, Timestamp, doc, getDoc } from 'firebase/firestore';
import { getEventsByUser } from '../services/eventService';
import { fetchGoogleCalendarEvents } from '../services/googleCalendarService';
import { getUserIntegrationsStatus } from '../services/userIntegrationService';
import { getAllInternalUsers } from '../services/internalUserService';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import {
  formatSessionDateLabel,
  formatSessionTimeLabel,
  getBreakthroughTodayStart,
  sessionToStartDate,
} from '../utils/breakthroughSessions';
import PortalBackground from '../components/PortalBackground';

// Custom CSS for dark mode calendar
const calendarDarkModeStyles = `
  .dark .rbc-calendar {
    background-color: #1f2937;
    color: #f9fafb;
  }
  
  .dark .rbc-header {
    background-color: #374151;
    color: #f9fafb;
    border-color: #4b5563;
  }
  
  .dark .rbc-month-view {
    background-color: #1f2937;
    border-color: #4b5563;
  }
  
  .dark .rbc-month-row {
    border-color: #4b5563;
  }
  
  .dark .rbc-date-cell {
    color: #f9fafb;
  }
  
  .dark .rbc-off-range-bg {
    background-color: #111827;
  }
  
  .dark .rbc-off-range {
    color: #6b7280;
  }
  
  .dark .rbc-today {
    background-color: #374151;
  }
  
  .dark .rbc-event {
    background-color: #dc2626;
    color: #ffffff;
  }
  
  .dark .rbc-event:hover {
    background-color: #b91c1c;
  }
  
  .dark .rbc-show-more {
    background-color: #374151;
    color: #f9fafb;
  }
  
  .dark .rbc-show-more:hover {
    background-color: #4b5563;
  }
  
  .dark .rbc-time-view {
    background-color: #1f2937;
    border-color: #4b5563;
  }
  
  .dark .rbc-time-header {
    background-color: #374151;
    border-color: #4b5563;
  }
  
  .dark .rbc-time-content {
    background-color: #1f2937;
    border-color: #4b5563;
  }
  
  .dark .rbc-timeslot-group {
    border-color: #4b5563;
  }
  
  .dark .rbc-time-slot {
    border-color: #4b5563;
  }
  
  .dark .rbc-day-slot {
    border-color: #4b5563;
  }
  
  .dark .rbc-time-gutter {
    background-color: #374151;
    color: #f9fafb;
  }
  
  .dark .rbc-label {
    color: #f9fafb;
  }
  
  .dark .rbc-toolbar {
    background-color: #1f2937;
    border-color: #4b5563;
  }
  
  .dark .rbc-toolbar button {
    background-color: #374151;
    color: #f9fafb;
    border-color: #4b5563;
  }
  
  .dark .rbc-toolbar button:hover {
    background-color: #4b5563;
  }
  
  .dark .rbc-toolbar button.rbc-active {
    background-color: #dc2626;
    color: #ffffff;
  }
  
  .dark .rbc-toolbar-label {
    color: #f9fafb;
  }
  
  .dark .rbc-time-header-content {
    border-color: #4b5563;
  }
  
  .dark .rbc-header {
    border-color: #4b5563;
  }
  
  .dark .rbc-month-header {
    border-color: #4b5563;
  }
  
  .dark .rbc-month-content {
    border-color: #4b5563;
  }
  
  .dark .rbc-month-row + .rbc-month-row {
    border-color: #4b5563;
  }
  
  .dark .rbc-date-cell.rbc-off-range {
    color: #6b7280;
  }
  
  .dark .rbc-date-cell.rbc-off-range.rbc-today {
    background-color: #374151;
  }
  
  .dark .rbc-overlay {
    background-color: #1f2937;
    border-color: #4b5563;
  }
  
  .dark .rbc-overlay-header {
    background-color: #374151;
    border-color: #4b5563;
  }
  
  .dark .rbc-overlay-content {
    background-color: #1f2937;
  }
  
  .dark .rbc-overlay-content .rbc-event {
    background-color: #dc2626;
    color: #ffffff;
  }
`;

// Initialize the calendar localizer
const locales = {
  'en-US': require('date-fns/locale/en-US'),
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});


const PortalCalendar = () => {
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  const { isMobile, isTablet } = useResponsive();
  const [featuredEvent, setFeaturedEvent] = useState(null);
  const [timeUntilFeatured, setTimeUntilFeatured] = useState('');
  const [nextBreakthroughSession, setNextBreakthroughSession] = useState(null);
  const [timeUntilBreakthrough, setTimeUntilBreakthrough] = useState('');
  const [breakthroughLoading, setBreakthroughLoading] = useState(false);
  const [showDepartmentModal, setShowDepartmentModal] = useState(false);
  const [showLearnMoreModal, setShowLearnMoreModal] = useState(false);
  const [pillarsSettings, setPillarsSettings] = useState({});
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [userData, setUserData] = useState(null);
  
  // Visit status state
  const [onsiteVisitStatus, setOnsiteVisitStatus] = useState('claim'); // 'claim', 'requested', 'approved', 'denied'
  const [cqcVisitStatus, setCqcVisitStatus] = useState('claim'); // 'claim', 'requested', 'approved', 'denied'

  // Shared Strategy Calls usage (company-wide Monthly 1:1) for schedule‑a‑call flows
  const { atLimit: strategyCallsAtLimit } = useStrategyCalls();

  // Helper function to get status pill styling and text
  const getStatusPill = (status) => {
    switch (status) {
      case 'claim':
        return {
          text: 'Claim Your Dates',
          className: 'bg-red-100 text-red-800 border-red-200'
        };
      case 'requested':
        return {
          text: 'Requested',
          className: 'bg-yellow-100 text-yellow-800 border-yellow-200'
        };
      case 'approved':
        return {
          text: 'Approved',
          className: 'bg-green-100 text-green-800 border-green-200'
        };
      case 'denied':
        return {
          text: 'Denied',
          className: 'bg-red-100 text-red-800 border-red-200'
        };
      default:
        return {
          text: 'Claim Your Dates',
          className: 'bg-red-100 text-red-800 border-red-200'
        };
    }
  };

  // Custom Toolbar component (defined inside component to access setCurrentDate)
  const CustomToolbar = ({ onNavigate, label, date, localizer }) => {
    const { darkMode } = useTheme();
    
    const goToBack = () => {
      const newDate = localizer.add(date, -1, 'month');
      onNavigate('PREV');
      setCurrentDate(newDate);
    };

    const goToNext = () => {
      const newDate = localizer.add(date, 1, 'month');
      onNavigate('NEXT');
      setCurrentDate(newDate);
    };

    return (
      <div className="flex items-center justify-between mb-4">
        <button 
          onClick={goToBack}
          className={`${isMobile ? 'p-2' : 'p-3'} rounded-full transition-colors duration-200 ${
            darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
          }`}
          aria-label="Previous month"
        >
          <FaChevronLeft className={`${isMobile ? 'text-lg' : 'text-xl'} ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} />
        </button>
        <span className={`${isMobile ? 'text-lg' : 'text-2xl'} font-bold tracking-tight text-gray-900 dark:text-white`}>
          {label}
        </span>
        <button 
          onClick={goToNext}
          className={`${isMobile ? 'p-2' : 'p-3'} rounded-full transition-colors duration-200 ${
            darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
          }`}
          aria-label="Next month"
        >
          <FaChevronRight className={`text-xl ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} />
        </button>
      </div>
    );
  };

  // Department cards - use same schedule links as portal pages (from pillars/routing config)
  const departmentCards = [
    { id: 'sales', title: 'Sales', icon: <FaDollarSign className="text-4xl" />, color: 'text-red-600', bgColor: 'bg-red-50' },
    { id: 'operations', title: 'Operations', icon: <FaCogs className="text-4xl" />, color: 'text-blue-600', bgColor: 'bg-blue-50' },
    { id: 'technology', title: 'Technology', icon: <FaMicrochip className="text-4xl" />, color: 'text-purple-600', bgColor: 'bg-purple-50' },
    { id: 'finance', title: 'Finance', icon: <FaWallet className="text-4xl" />, color: 'text-green-600', bgColor: 'bg-green-50' },
    { id: 'marketing', title: 'Marketing', icon: <FaBullhorn className="text-4xl" />, color: 'text-orange-600', bgColor: 'bg-orange-50' },
    { id: 'hr', title: 'HR', icon: <FaUsers className="text-4xl" />, color: 'text-yellow-600', bgColor: 'bg-yellow-50' }
  ];

  // Fetch user events
  const fetchUserEvents = async (date = new Date()) => {
    if (!currentUser) return;
    
    try {
      setEventsLoading(true);
      
      // Get date range for the month view (past 6 months to future 6 months for better coverage)
      const monthStart = startOfMonth(date);
      const monthEnd = endOfMonth(date);
      const startDate = new Date(monthStart);
      startDate.setMonth(startDate.getMonth() - 6);
      const endDate = new Date(monthEnd);
      endDate.setMonth(endDate.getMonth() + 6);
      
      // Fetch local events
      const localEvents = await getEventsByUser(currentUser.uid);
      
      // Filter local events to the date range
      const filteredLocalEvents = localEvents.filter(event => {
        const eventStart = event.startTime || event.start;
        if (!eventStart) return false;
        const eventDate = new Date(eventStart);
        return eventDate >= startDate && eventDate <= endDate;
      });
      
      // Transform local events to calendar format
      const transformedLocalEvents = filteredLocalEvents.map(event => ({
        id: event.id,
        title: event.title || 'No Title',
        start: event.startTime || event.start,
        end: event.endTime || event.end || event.startTime || event.start,
        description: event.description || '',
        location: event.location || '',
        isGoogleEvent: false,
        source: 'local',
        ...event
      }));
      
      // Check Google Calendar connection and fetch Google events
      let googleEvents = [];
      try {
        const integrationsStatus = await getUserIntegrationsStatus(currentUser.uid);
        const isConnected = integrationsStatus?.googleCalendar?.connected || false;
        
        if (isConnected) {
          googleEvents = await fetchGoogleCalendarEvents(currentUser.uid, startDate, endDate);
          console.log('Google Calendar events fetched:', googleEvents);
        }
      } catch (googleError) {
        console.error('Error fetching Google Calendar events:', googleError);
        // Don't fail the entire function if Google Calendar fails
        googleEvents = [];
      }
      
      // Transform Google events to ensure they have the right format
      const transformedGoogleEvents = googleEvents.map(event => ({
        id: event.id || `google_${Date.now()}_${Math.random()}`,
        title: event.title || 'No Title',
        start: event.start ? new Date(event.start) : null,
        end: event.end ? new Date(event.end) : (event.start ? new Date(event.start) : null),
        description: event.description || '',
        location: event.location || '',
        isGoogleEvent: true,
        source: 'google',
        ...event
      }));
      
      // Fetch events from all internal users' Google Calendars where client is an attendee
      let internalUserEvents = [];
      try {
        // Get client's email
        let clientEmail = currentUser.email?.toLowerCase();
        if (!clientEmail) {
          // Try to get email from users collection
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            clientEmail = userData.email?.toLowerCase();
          }
        }
        
        if (clientEmail) {
          // Get all internal users
          const internalUsers = await getAllInternalUsers();
          
          // Fetch events from each internal user's Google Calendar
          for (const user of internalUsers) {
            try {
              const userEvents = await fetchGoogleCalendarEvents(user.id, startDate, endDate);
              
              // Filter events where client email is in attendees
              const clientEvents = userEvents.filter(event => {
                const attendees = event.attendees || [];
                return attendees.some(attendee => 
                  attendee.email?.toLowerCase() === clientEmail
                );
              });
              
              // Transform to calendar format
              const transformedClientEvents = clientEvents.map(event => ({
                id: event.id || `internal_google_${user.id}_${Date.now()}_${Math.random()}`,
                title: event.title || 'No Title',
                start: event.start ? new Date(event.start) : null,
                end: event.end ? new Date(event.end) : (event.start ? new Date(event.start) : null),
                description: event.description || '',
                location: event.location || '',
                isGoogleEvent: true,
                source: 'internal_google',
                organizer: event.organizer,
                ...event
              }));
              
              internalUserEvents.push(...transformedClientEvents);
            } catch (error) {
              console.error(`Error fetching events for internal user ${user.id}:`, error);
              // Continue with other users even if one fails
            }
          }
        }
      } catch (error) {
        console.error('Error fetching internal user events:', error);
        // Don't fail the entire function if internal user events fail
        internalUserEvents = [];
      }
      
      // Merge local, client's Google, and internal users' Google events
      const allEvents = [...transformedLocalEvents, ...transformedGoogleEvents, ...internalUserEvents];
      
      // Filter out events without valid start dates
      const validEvents = allEvents.filter(event => event.start && !isNaN(new Date(event.start).getTime()));
      
      // Sort events by start time
      validEvents.sort((a, b) => {
        const aTime = new Date(a.start);
        const bTime = new Date(b.start);
        return aTime - bTime;
      });
      
      setEvents(validEvents);
    } catch (err) {
      console.error('Error fetching user events:', err);
      setEvents([]);
    } finally {
      setEventsLoading(false);
    }
  };

  // Inject dark mode styles for calendar
  useEffect(() => {
    const styleId = 'portal-calendar-dark-mode-styles';
    let styleElement = document.getElementById(styleId);
    
    if (darkMode) {
      if (!styleElement) {
        styleElement = document.createElement('style');
        styleElement.id = styleId;
        document.head.appendChild(styleElement);
      }
      styleElement.textContent = calendarDarkModeStyles;
    } else {
      if (styleElement) {
        styleElement.remove();
      }
    }
    
    return () => {
      if (styleElement) {
        styleElement.remove();
      }
    };
  }, [darkMode]);

  // Fetch events when component mounts or currentDate changes
  useEffect(() => {
    if (currentUser) {
      fetchUserEvents(currentDate);
    }
  }, [currentUser, currentDate]);

  useEffect(() => {
    // Find next event, preferring breakthrough events
    const now = new Date();
    const upcomingEvents = events
      .filter(event => {
        const eventStart = event.start ? new Date(event.start) : null;
        return eventStart && eventStart > now;
      })
      .sort((a, b) => {
        const aTime = new Date(a.start);
        const bTime = new Date(b.start);
        return aTime - bTime;
      });
    
    const breakthroughEvents = upcomingEvents.filter(ev =>
      ev.breakthrough || (ev.title && ev.title.toLowerCase().includes('breakthrough'))
    );

    const next = breakthroughEvents[0] || upcomingEvents[0] || null;
    setFeaturedEvent(next);

    // Update countdown timer
    const updateCountdowns = () => {
      if (next && next.start) {
        const eventStart = new Date(next.start);
        const diff = eventStart - new Date();
        if (diff > 0) {
          const days = Math.floor(diff / (1000 * 60 * 60 * 24));
          const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
          setTimeUntilFeatured(`${days}d ${hours}h`);
        } else {
          setTimeUntilFeatured('0d 0h');
        }
      } else {
        setTimeUntilFeatured('');
      }
    };

    updateCountdowns();
    const interval = setInterval(updateCountdowns, 1000 * 60); // Update every minute

    return () => clearInterval(interval);
  }, [events]);

  const pickProfilePicUrl = (data) => {
    if (!data) return '';
    return data.profileImage || data.profilePicture || data.photoURL || data.photoUrl || data.avatarUrl || data.avatarURL || '';
  };

  const initialsFor = (presenter, fallback) => {
    if (!presenter) return fallback;
    return presenter.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();
  };

  const deptStyles = {
    Sales: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200',
    Operations: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200',
    Finance: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200',
    Technology: 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-200',
    Marketing: 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-200',
    HR: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-200',
    General: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  };

  const avatarStyles = [
    'bg-gradient-to-br from-red-500 to-pink-500',
    'bg-gradient-to-br from-purple-500 to-indigo-500',
    'bg-gradient-to-br from-orange-500 to-amber-500',
    'bg-gradient-to-br from-teal-500 to-cyan-500',
  ];

  // Fetch next breakthrough session from breakthroughSessions collection
  useEffect(() => {
    const fetchNextBreakthroughSession = async () => {
      try {
        setBreakthroughLoading(true);
        setNextBreakthroughSession(null);

        const today = getBreakthroughTodayStart();

        const q = query(
          collection(db, 'breakthroughSessions'),
          where('date', '>=', Timestamp.fromDate(today)),
          orderBy('date', 'asc'),
          limit(1)
        );

        const snap = await getDocs(q);
        if (snap.empty) {
          setNextBreakthroughSession(null);
          return;
        }

        const docSnap = snap.docs[0];
        const data = docSnap.data();
        let session = { id: docSnap.id, ...data };

        if (session.presenterId && !session.presenterPhoto) {
          try {
            const userSnap = await getDoc(doc(db, 'internal_users', session.presenterId));
            if (userSnap.exists()) {
              session = { ...session, presenterPhoto: pickProfilePicUrl(userSnap.data()) || session.presenterPhoto };
            }
          } catch {
            // keep session as-is
          }
        }

        setNextBreakthroughSession(session);
      } catch (err) {
        console.error('Error fetching next breakthrough session:', err);
        setNextBreakthroughSession(null);
      } finally {
        setBreakthroughLoading(false);
      }
    };

    fetchNextBreakthroughSession();
  }, []);

  // Countdown for next breakthrough session
  useEffect(() => {
    const updateCountdown = () => {
      const start = sessionToStartDate(nextBreakthroughSession);
      if (!start) {
        setTimeUntilBreakthrough('');
        return;
      }
      const diff = start - new Date();
      if (diff > 0) {
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        setTimeUntilBreakthrough(`${days}d ${hours}h`);
      } else {
        setTimeUntilBreakthrough('0d 0h');
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000 * 60);
    return () => clearInterval(interval);
  }, [nextBreakthroughSession]);

  // Load Calendly widget script and styles for Schedule a Call popup
  useEffect(() => {
    if (document.getElementById('calendly-widget-css')) return;
    const link = document.createElement('link');
    link.id = 'calendly-widget-css';
    link.href = 'https://assets.calendly.com/assets/external/widget.css';
    link.rel = 'stylesheet';
    document.head.appendChild(link);

    if (document.getElementById('calendly-widget-js')) return;
    const script = document.createElement('script');
    script.id = 'calendly-widget-js';
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.head.appendChild(script);
  }, []);

  // Load user profile for Calendly prefill
  useEffect(() => {
    const loadUserData = async () => {
      if (!currentUser) return;
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) setUserData(userDoc.data());
      } catch (error) {
        console.error('Error loading user data:', error);
      }
    };
    loadUserData();
  }, [currentUser]);

  // Load pillars settings
  useEffect(() => {
    const loadPillarsSettings = async () => {
      if (!currentUser) return;
      
      try {
        const settings = {};
        for (const pillar of ['sales', 'operations', 'technology', 'finance', 'marketing', 'hr']) {
          settings[pillar] = await getPillarScheduleLink(currentUser.uid, pillar);
        }
        setPillarsSettings(settings);
      } catch (error) {
        console.error('Error loading pillars settings:', error);
      }
    };

    loadPillarsSettings();
  }, [currentUser]);

  const handleScheduleMeeting = () => {
    if (strategyCallsAtLimit) return;
    setShowDepartmentModal(true);
  };

  const handleCloseModal = () => {
    setShowDepartmentModal(false);
  };

  const handleDepartmentSelect = (department) => {
    const baseUrl = pillarsSettings[department.id];
    if (!baseUrl) return;

    const firstName = userData?.firstName?.trim() || currentUser?.displayName?.split(/\s+/)[0] || currentUser?.email?.split('@')[0] || '';
    const lastName = userData?.lastName?.trim() || (currentUser?.displayName?.split(/\s+/)?.slice(1)?.join(' ') || '') || '';
    const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
    const prefill = (currentUser || userData) ? {
      firstName,
      lastName,
      name: fullName || currentUser?.displayName || currentUser?.email?.split('@')[0] || '',
      email: currentUser?.email || '',
      phone: userData?.phone?.trim() || ''
    } : {};
    const hasPrefill = prefill.name || prefill.firstName || prefill.lastName || prefill.email || prefill.phone;
    const isCalendlyUrl = baseUrl.includes('calendly.com');

    if (isCalendlyUrl && window.Calendly) {
      window.Calendly.initPopupWidget({
        url: baseUrl,
        prefill: hasPrefill ? prefill : undefined
      });
    } else {
      if (isCalendlyUrl) {
        const params = new URLSearchParams();
        if (prefill.name) params.set('name', prefill.name);
        if (prefill.email) params.set('email', prefill.email);
        if (prefill.firstName) params.set('first_name', prefill.firstName);
        if (prefill.lastName) params.set('last_name', prefill.lastName);
        window.open(params.toString() ? `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}${params}` : baseUrl, '_blank');
      } else {
        window.open(baseUrl, '_blank');
      }
    }
    handleCloseModal();
  };

  // Load visit status from Firebase
  useEffect(() => {
    const loadVisitStatus = async () => {
      if (!currentUser) return;
      
      try {
        // Check for onsite visit requests
        const onsiteQuery = query(
          collection(db, 'onSiteRequests'), // Using the correct collection name
          where('userId', '==', currentUser.uid)
        );
        const onsiteSnapshot = await getDocs(onsiteQuery);
        
        if (!onsiteSnapshot.empty) {
          const onsiteDoc = onsiteSnapshot.docs[0];
          const onsiteData = onsiteDoc.data();
          
          // Map the status from Firebase to our display status
          if (onsiteData.status === 'approved') {
            setOnsiteVisitStatus('approved');
          } else if (onsiteData.status === 'denied') {
            setOnsiteVisitStatus('denied');
          } else if (onsiteData.status === 'pending') {
            setOnsiteVisitStatus('requested');
          } else {
            setOnsiteVisitStatus('claim');
          }
        } else {
          setOnsiteVisitStatus('claim');
        }

        // Check for CQC visit requests
        const cqcQuery = query(
          collection(db, 'cqcVisitRequests'), // Using the correct collection name
          where('userId', '==', currentUser.uid)
        );
        const cqcSnapshot = await getDocs(cqcQuery);
        
        if (!cqcSnapshot.empty) {
          const cqcDoc = cqcSnapshot.docs[0];
          const cqcData = cqcDoc.data();
          
          // Map the status from Firebase to our display status
          if (cqcData.status === 'approved') {
            setCqcVisitStatus('approved');
          } else if (cqcData.status === 'denied') {
            setCqcVisitStatus('denied');
          } else if (cqcData.status === 'pending') {
            setCqcVisitStatus('requested');
          } else {
            setCqcVisitStatus('claim');
          }
        } else {
          setCqcVisitStatus('claim');
        }
      } catch (error) {
        console.error('Error loading visit status:', error);
        // Fallback to default status
        setOnsiteVisitStatus('claim');
        setCqcVisitStatus('claim');
      }
    };

    loadVisitStatus();
  }, [currentUser]);

  return (
    <div className={`relative min-h-screen ${isMobile ? 'p-3' : isTablet ? 'p-4' : 'p-6'}`}>
      <PortalBackground />
      <div className="relative">
      {/* Page Header with Centered Title and Top Right Button */}
      <div className={`relative ${isMobile ? 'mb-4' : 'mb-6'}`}>
        <div className="text-center">
          <h1 className={`${isMobile ? 'text-xl' : isTablet ? 'text-2xl' : 'text-3xl'} font-bold tracking-tight text-gray-900 dark:text-white`}>Calendar</h1>
        </div>
        
        {/* Global Add Button - Top Right */}
        <button
          onClick={handleScheduleMeeting}
          className={`absolute top-0 right-0 ${isMobile ? 'w-10 h-10' : 'w-12 h-12'} rounded-full shadow-lg transition-all duration-200 flex items-center justify-center ${
            strategyCallsAtLimit
              ? darkMode
                ? 'bg-gray-600 text-gray-300 cursor-not-allowed opacity-70'
                : 'bg-gray-300 text-gray-600 cursor-not-allowed opacity-70'
              : darkMode 
                ? 'bg-red-600 hover:bg-red-700 text-white shadow-red-900/50 hover:scale-110' 
                : 'bg-red-600 hover:bg-red-700 text-white shadow-red-600/30 hover:scale-110'
          }`}
          aria-label="Schedule Meeting"
        >
          <FaPlus className={isMobile ? 'text-lg' : 'text-xl'} />
        </button>
      </div>
      
      {/* Visit Cards */}
      <div className={`grid ${isMobile ? 'grid-cols-1 gap-3' : 'grid-cols-1 md:grid-cols-4 gap-6'} ${isMobile ? 'mb-4' : 'mb-6'}`}>
        {/* Next Breakthrough Session Countdown */}
        <div className={`rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm ${isMobile ? 'p-4' : 'p-6'} ${isMobile ? '' : 'md:col-span-2'}`}>
          <h2 className={`text-xl font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>Next Breakthrough Session</h2>
          {breakthroughLoading ? (
            <p className={`text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Loading…</p>
          ) : nextBreakthroughSession ? (
            <div className="space-y-4">
              <div className="flex items-start gap-2">
                {(nextBreakthroughSession.presenter || nextBreakthroughSession.needPresenter) && (
                  <div className="flex-shrink-0 flex flex-col items-center">
                    {nextBreakthroughSession.needPresenter ? (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                        Need Presenter
                      </span>
                    ) : (
                      <>
                        {nextBreakthroughSession.presenterPhoto ? (
                          <img
                            src={nextBreakthroughSession.presenterPhoto}
                            alt={nextBreakthroughSession.presenter || 'Presenter'}
                            className="h-16 w-16 rounded-full object-cover"
                          />
                        ) : (
                          <div
                            className={`h-16 w-16 rounded-full flex items-center justify-center text-white font-semibold text-base ${
                              avatarStyles[
                                nextBreakthroughSession.presenter?.length
                                  ? Math.abs(nextBreakthroughSession.presenter.charCodeAt(0)) % avatarStyles.length
                                  : (sessionToStartDate(nextBreakthroughSession)?.getMonth() ?? 0) % avatarStyles.length
                              ]
                            }`}
                          >
                            {nextBreakthroughSession.presenterInitials ||
                              initialsFor(nextBreakthroughSession.presenter, '—')}
                          </div>
                        )}
                        <span className={`text-base font-medium mt-1 text-center ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          {nextBreakthroughSession.presenter}
                        </span>
                      </>
                    )}
                    {nextBreakthroughSession.department && (
                      <span className={`mt-2 inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${deptStyles[nextBreakthroughSession.department] || deptStyles.General}`}>
                        {nextBreakthroughSession.department}
                      </span>
                    )}
                  </div>
                )}
                <div className="flex-1 min-w-0 flex flex-col items-center text-center">
                  <h3 className={`text-lg font-medium ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                    {nextBreakthroughSession.eventName || 'Breakthrough Session'}
                  </h3>
                  {!nextBreakthroughSession.presenter && !nextBreakthroughSession.needPresenter && nextBreakthroughSession.department && (
                    <span className={`mt-1 inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${deptStyles[nextBreakthroughSession.department] || deptStyles.General}`}>
                      {nextBreakthroughSession.department}
                    </span>
                  )}
                  <div className="mt-2 flex flex-wrap items-center justify-center gap-4">
                    <p className={`text-2xl font-bold tracking-tight text-gray-900 dark:text-white`}>{timeUntilBreakthrough}</p>
                    {sessionToStartDate(nextBreakthroughSession) && (
                      <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        <p>{formatSessionDateLabel(nextBreakthroughSession)}</p>
                        {formatSessionTimeLabel(nextBreakthroughSession) && (
                          <p>{formatSessionTimeLabel(nextBreakthroughSession)}</p>
                        )}
                      </div>
                    )}
                  </div>
                  {nextBreakthroughSession.description && (
                    <button
                      onClick={() => setShowLearnMoreModal(true)}
                      className={`mt-3 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        darkMode
                          ? 'bg-blue-600 hover:bg-blue-700 text-white'
                          : 'bg-blue-600 hover:bg-blue-700 text-white'
                      }`}
                    >
                      Learn More
                    </button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <p className={`text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No upcoming events</p>
          )}
        </div>

        {/* On-Site Visit Card */}
        <div className={`rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm ${isMobile ? 'p-4' : 'p-6'} cursor-pointer transition-all duration-200 ${isMobile ? '' : 'hover:scale-105'}`} onClick={() => window.location.href = '/portal/bookonsite'}>
          <div className="text-center">
            <div className="mb-4 flex justify-center text-green-600">
              <FaMapMarkerAlt className="text-4xl" />
            </div>
            <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>On-Site Visit</h3>
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'} mb-4`}>We Come To You</p>
            {/* Status Display */}
            <div className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium border ${getStatusPill(onsiteVisitStatus).className}`}>
              {getStatusPill(onsiteVisitStatus).text}
            </div>
          </div>
        </div>

        {/* CQC Visit Card */}
        <div className={`rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-6 cursor-pointer transition-all duration-200 hover:shadow-md hover:-translate-y-0.5`} onClick={() => window.location.href = '/portal/bookcqc'}>
          <div className="text-center">
            <div className="mb-4 flex justify-center text-red-600">
              <FaUsers className="text-4xl" />
            </div>
            <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>CQC Visit</h3>
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'} mb-4`}>You Come to Us</p>
            {/* Status Display */}
            <div className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium border ${getStatusPill(cqcVisitStatus).className}`}>
              {getStatusPill(cqcVisitStatus).text}
            </div>
          </div>
        </div>
      </div>

      {/* Calendar */}
      <div className={`rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm ${isMobile ? 'p-4' : 'p-6'}`}>
        {eventsLoading ? (
          <div className="flex justify-center items-center" style={{ height: 'calc(100vh - 400px)' }}>
            <BBALoader size={48} />
          </div>
        ) : (
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: 'calc(100vh - 400px)' }}
            defaultView="month"
            views={['month']}
            onNavigate={(date) => setCurrentDate(date)}
            date={currentDate}
            onSelectEvent={(event) => {
              setSelectedEvent(event);
              setShowEventModal(true);
            }}
            tooltipAccessor={(event) => {
              const desc = event.description ? `\n${event.description}` : '';
              const loc = event.location ? `\n📍 ${event.location}` : '';
              return `${event.title}${desc}${loc}`;
            }}
            components={{
              toolbar: (props) => <CustomToolbar {...props} />
            }}
            className={darkMode ? 'dark' : ''}
            eventPropGetter={(event) => {
              const isBreakthrough =
                event.breakthrough ||
                (event.title && event.title.toLowerCase().includes('breakthrough'));

              // Breakthrough events are always red (trumps Google/default color)
              if (isBreakthrough) {
                return {
                  style: {
                    backgroundColor: '#dc2626',
                    color: '#ffffff',
                    border: '1px solid #b91c1c',
                    borderRadius: '4px'
                  }
                };
              }

              // Internal user Google Calendar events (events where client is attendee)
              if (event.source === 'internal_google') {
                return {
                  style: {
                    backgroundColor: '#9333ea', // Purple color for internal user events
                    color: '#ffffff',
                    border: '1px solid #7e22ce',
                    borderRadius: '4px'
                  }
                };
              }
              
              // Client's own Google Calendar events use blue
              if (event.isGoogleEvent || event.source === 'google') {
                return {
                  style: {
                    backgroundColor: '#4285f4',
                    color: '#ffffff',
                    border: '1px solid #1a73e8',
                    borderRadius: '4px'
                  }
                };
              }

              // Default styling for local events
              return {
                style: {
                  backgroundColor: '#10b981',
                  color: '#ffffff',
                  border: '1px solid #059669',
                  borderRadius: '4px'
                }
              };
            }}
          />
        )}
      </div>

      {/* Event Details Modal */}
      {showEventModal && selectedEvent && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowEventModal(false)}>
          <div 
            className={`w-full max-w-2xl mx-4 rounded-lg shadow-xl ${darkMode ? 'bg-gray-800' : 'bg-white'} max-h-[90vh] overflow-y-auto`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="relative mb-6">
                <h2 className={`text-2xl font-bold tracking-tight text-center text-gray-900 dark:text-white`}>
                  {(() => {
                    const eventTitle = selectedEvent.title || 'Event Details';
                    // Remove time prefix if it exists (from Google Calendar service formatting)
                    return eventTitle.replace(/^\d{1,2}:\d{2}\s*(AM|PM)\s*-\s*/, '');
                  })()}
                </h2>
                <button
                  onClick={() => setShowEventModal(false)}
                  className={`absolute top-0 right-0 p-2 rounded-full transition-colors duration-200 ${
                    darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                  }`}
                >
                  <FaPlus className={`text-xl transform rotate-45 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
                </button>
              </div>

              <div className="space-y-4">
                {/* Date/Time and Organizer in same row */}
                <div className="flex flex-wrap gap-6 items-start">
                  {selectedEvent.start && (
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-center space-x-2 mb-2">
                        <FaCalendarAlt className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
                        <h3 className={`font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Date & Time</h3>
                      </div>
                      <div className={`text-center ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        <p>{format(new Date(selectedEvent.start), 'EEEE, MMMM d, yyyy')}</p>
                        <div className="flex items-center justify-center space-x-2 mt-1">
                          <FaClock className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                          <p>
                            {format(new Date(selectedEvent.start), 'h:mm a')}
                            {selectedEvent.end && ` - ${format(new Date(selectedEvent.end), 'h:mm a')}`}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  {selectedEvent.source === 'internal_google' && selectedEvent.organizer && (
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-center space-x-2 mb-2">
                        <FaUsers className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
                        <h3 className={`font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Organizer</h3>
                      </div>
                      <p className={`text-center ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        {selectedEvent.organizer.displayName || selectedEvent.organizer.email || 'Unknown'}
                      </p>
                    </div>
                  )}
                </div>

                {/* Location */}
                {selectedEvent.location && (
                  <div>
                    <div className="flex items-center justify-center space-x-2 mb-2">
                      <FaMapMarkerAlt className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
                      <h3 className={`font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Location</h3>
                    </div>
                    <p className={`text-center ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{selectedEvent.location}</p>
                  </div>
                )}

                {/* Description */}
                {selectedEvent.description && (
                  <div>
                    <h3 className={`font-semibold mb-2 text-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Description</h3>
                    <p className={`whitespace-pre-wrap text-center ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      {selectedEvent.description}
                    </p>
                  </div>
                )}

                {/* Google Calendar Link and Google Meet Link */}
                {(selectedEvent.htmlLink || selectedEvent.hangoutLink) && (
                  <div className="pt-4 flex flex-wrap gap-3 justify-center">
                    {selectedEvent.htmlLink && (
                      <a
                        href={selectedEvent.htmlLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`inline-flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                          darkMode
                            ? 'bg-blue-600 hover:bg-blue-700 text-white'
                            : 'bg-blue-600 hover:bg-blue-700 text-white'
                        }`}
                      >
                        <FaCalendarAlt />
                        <span>Open in Google Calendar</span>
                      </a>
                    )}
                    {selectedEvent.hangoutLink && (
                      <a
                        href={selectedEvent.hangoutLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`inline-flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                          darkMode
                            ? 'bg-green-600 hover:bg-green-700 text-white'
                            : 'bg-green-600 hover:bg-green-700 text-white'
                        }`}
                      >
                        <FaUsers />
                        <span>Join Google Meet</span>
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Learn More Modal - Breakthrough Session Description */}
      {showLearnMoreModal && nextBreakthroughSession?.description && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowLearnMoreModal(false)}>
          <div
            className={`w-full max-w-md mx-4 rounded-lg shadow-xl p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-4">
              <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {nextBreakthroughSession.eventName || 'Breakthrough Session'}
              </h3>
              <button
                onClick={() => setShowLearnMoreModal(false)}
                className={`p-2 rounded-full transition-colors ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
              >
                <FaPlus className={`text-xl transform rotate-45 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
              </button>
            </div>
            <p className={`text-sm leading-relaxed whitespace-pre-wrap ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {nextBreakthroughSession.description}
            </p>
          </div>
        </div>
      )}

      {/* Department Selection Modal */}
      {showDepartmentModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className={`w-full max-w-4xl mx-4 rounded-lg shadow-xl ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="p-6">
              <div className="relative mb-6">
                <h2 className={`text-2xl font-bold tracking-tight text-center text-gray-900 dark:text-white`}>
                  Choose Department
                </h2>
                <button
                  onClick={handleCloseModal}
                  className={`absolute top-0 right-0 p-2 rounded-full transition-colors duration-200 ${
                    darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                  }`}
                >
                  <FaPlus className={`text-xl transform rotate-45 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
                </button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                 {departmentCards.map((department) => (
                                      <div
                      key={department.id}
                      onClick={() => handleDepartmentSelect(department)}
                      className={`p-6 rounded-lg border-2 border-dashed cursor-pointer transition-all duration-200 hover:scale-105 ${
                        darkMode 
                          ? 'border-gray-600 hover:border-gray-500 hover:bg-gray-700' 
                          : `${department.bgColor} border-gray-300 hover:border-gray-400 hover:bg-gray-100`
                      }`}
                    >
                      <div className="text-center">
                        <div className={`mb-4 flex justify-center ${department.color}`}>
                          {department.icon}
                        </div>
                        <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {department.title}
                        </h3>
                      </div>
                    </div>
                 ))}
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default PortalCalendar; 